import { useMemo } from 'react';
import { apiGetUserCreditBalance, apiListUserCreditGrants, apiRechargeUserCreditBalance } from 'api/users/userCredits';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { formatCentsToDollars } from 'utils/pricing';
import { queryKeys } from 'utils/react-query';
import { useCurrentUserId } from 'modules/Auth/hooks';

interface RechargeCreditOptions {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

interface RechargePayload {
  amount: number;
  description: string;
}

type Config = {
  disableCreditGrantFetch?: boolean;
};

const queryConfig = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  cacheTime: 10 * 60 * 1000, // 10 minutes
  refetchOnWindowFocus: false,
  refetchOnMount: false,
};

export const useCreditBalance = ({ disableCreditGrantFetch = false }: Config = {}) => {
  const userId = useCurrentUserId();
  const queryClient = useQueryClient();

  const {
    data: creditBalanceData,
    isLoading: isLoadingBalance,
    refetch: refetchCreditBalance,
    error: balanceError,
  } = useQuery([queryKeys.userCreditBalance], () => apiGetUserCreditBalance({ userId }), {
    enabled: !!userId,
    ...queryConfig,
  });

  const {
    data: creditGrantsData,
    isLoading: isLoadingCreditGrants,
    error: grantsError,
  } = useQuery([queryKeys.UserCreditGrants], () => apiListUserCreditGrants({ userId }), {
    enabled: !!userId && !disableCreditGrantFetch,
    ...queryConfig,
  });

  const {
    mutate: rechargeMutate,
    isLoading: isRechargingBalance,
    error: rechargeError,
  } = useMutation({
    mutationFn: (body: Pick<CF.API.Users.IPostCreditBalance, 'credit_grants' | 'idempotency_key'>) => apiRechargeUserCreditBalance({ userId, body }),
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.userCreditBalance]);
      queryClient.invalidateQueries([queryKeys.UserCreditGrants]);
    },
  });

  const generateIdempotencyKey = (): string => {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random()}`;
  };

  const rechargeCreditBalance = (payload: RechargePayload, options?: RechargeCreditOptions) => {
    const body: Pick<CF.API.Users.IPostCreditBalance, 'credit_grants' | 'idempotency_key'> = {
      credit_grants: [
        {
          amount: payload.amount,
          description: payload.description,
        },
      ],
      idempotency_key: generateIdempotencyKey(),
    };

    rechargeMutate(body, {
      onSuccess: () => {
        options?.onSuccess?.();
        refetchCreditBalance();
      },
      onError: (error) => {
        options?.onError?.(error);
      },
    });
  };

  const formattedCreditGrants = useMemo(
    () =>
      creditGrantsData?.credit_grants?.map((grant: CF.API.Users.CreditGrant) => ({
        ...grant,
        amount: formatCentsToDollars(grant.amount),
        total_amount: formatCentsToDollars(grant.total_amount),
      })) ?? [],
    [creditGrantsData?.credit_grants],
  );

  return {
    creditBalance: formatCentsToDollars(creditBalanceData?.credit_balance ?? 0),
    creditBalanceRaw: creditBalanceData?.credit_balance ?? 0,
    creditGrants: formattedCreditGrants,
    isLoadingBalance,
    isLoadingCreditGrants,
    isRechargingBalance,
    balanceError,
    grantsError,
    rechargeError,
    rechargeCreditBalance,
    refetchCreditBalance,
  };
};
